/**
 * Hook do fluxo realizado (Omie) + projetado (Zeus) do mes corrente
 * (Task #132 — substitui projecao Zeus rotulada como "Receita ja
 * realizada" nos cards Entradas/Saidas).
 *
 * Mesmo padrao dos hooks existentes: cache module-level + refetch
 * periodico. TTL 1 minuto — Omie atualiza com o cron diario 07:15 BRT
 * mas o card precisa refletir uma virada de mes ou um lancamento
 * manual no Omie sem precisar refresh manual.
 *
 * Em caso de falha (rede/auth/5xx) devolve null em data + flag de erro.
 * Card decide se renderiza fallback ou estado "—".
 */
import { useEffect, useState } from "react";
import { getFluxoV2 } from "../api/client";
import type { FluxoV2Response } from "../api/types";
import { startVisiblePolling } from "./visiblePolling";

const REFETCH_MS = 180 * 1000; // #75: 60s->180s (fonte muda via cron diario + sync manual)

interface CacheEntry {
  expiresAt: number;
  promise: Promise<FluxoV2Response>;
}

// Cache chaveado por `dias` (janela do grafico — toggle 30/60/90): trocar a
// janela re-fetcha, mas voltar pra uma ja vista reusa o cache (sem flicker).
// Os campos do MES (cards Entradas/Saidas) sao identicos em qualquer janela;
// so as series diarias mudam — entao 1 entrada por janela basta.
const cacheByDias = new Map<number, CacheEntry>();

export function invalidateFluxoV2Cache(): void {
  cacheByDias.clear();
}

export interface UseFluxoV2State {
  data: FluxoV2Response | null;
  isLoading: boolean;
  error: Error | null;
}

/** `dias` (30/60/90) = janela das series diarias do grafico (toggle). Default
 *  30 (compat com os chamadores que so querem os cards do mes). */
export function useFluxoV2(dias = 30): UseFluxoV2State {
  const [state, setState] = useState<UseFluxoV2State>({
    data: null,
    isLoading: true,
    error: null,
  });

  useEffect(() => {
    let cancel = false;
    // Troca de janela: volta pro estado de loading (a serie vai mudar) sem
    // limpar o ultimo dado da janela anterior bruscamente — o setState abaixo
    // assume quando a promise resolve.
    setState((s) => ({ ...s, isLoading: true }));

    function load() {
      const now = Date.now();
      const cached = cacheByDias.get(dias);
      if (!cached || cached.expiresAt < now) {
        cacheByDias.set(dias, {
          expiresAt: now + REFETCH_MS,
          promise: getFluxoV2(undefined, dias),
        });
      }
      cacheByDias.get(dias)!.promise
        .then((data) => {
          if (cancel) return;
          setState({ data, isLoading: false, error: null });
        })
        .catch((err: Error) => {
          if (cancel) return;
          cacheByDias.delete(dias);
          setState({ data: null, isLoading: false, error: err });
        });
    }

    load();
    // #75: tick skipa com a aba em background; volta visivel/focus -> 1 load.
    const stop = startVisiblePolling(load, REFETCH_MS);
    return () => {
      cancel = true;
      stop();
    };
  }, [dias]);

  return state;
}
